import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { apiPatientReadingsChart } from 'services/dataService/resources/oneplatform/api';
import dataService from 'services/dataService';

import {
  mapTrendingChartResponse,
  toTimestampParam,
} from '../utils/trending-chart';

import type {
  IFetchTrendingChartParams,
  ITrendingChartApiResponse,
  ITrendingChartPayload,
} from 'components/molecules/oneplaform/trending-chart/types';
import type { TFunction } from 'i18next';
import type { TDateRange } from './use-timeframe-picker';

const TRENDING_CHART_QUERY_KEY = 'home-dashboard/trending-chart';

export const fetchTrendingChartData = async ({
  from,
  to,
  t,
}: IFetchTrendingChartParams): Promise<ITrendingChartPayload> => {
  const endpoint = apiPatientReadingsChart({
    startTime: toTimestampParam(from, 'start'),
    endTime: toTimestampParam(to, 'end'),
  });

  const { data, error } = await dataService.getList<ITrendingChartApiResponse>(
    endpoint,
  );

  if (error) {
    throw new Error(error);
  }

  return mapTrendingChartResponse(data, t);
};

export const useTrendingChart = ({
  t,
  dateRange,
}: {
  t: TFunction;
  dateRange: TDateRange | null;
}) => {
  const queryResult = useQuery<ITrendingChartPayload, Error>({
    queryKey: [
      TRENDING_CHART_QUERY_KEY,
      dateRange?.start ?? null,
      dateRange?.end ?? null,
    ],
    queryFn: () =>
      fetchTrendingChartData({
        from: dateRange?.start ?? '',
        to: dateRange?.end ?? '',
        t,
      }),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (queryResult.error) {
      const errorMessage =
        queryResult.error instanceof Error
          ? queryResult.error.message
          : String(queryResult.error);
      showResult(errorMessage);
    }
  }, [queryResult.error]);

  const chartData = queryResult.data;

  return {
    categories: chartData?.labels,
    series: chartData?.series,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
  };
};
